import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  InputAdornment,
} from "@mui/material";
import { EmailRounded, LockResetRounded } from "@mui/icons-material";
import SocialContainer from "../containers/SocialContainer";
import { getErrorResponse, getHeader } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";

const PasswordRecovery = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/api/request-password-link`,
        { email }
      );
      toast.success(response.data.message);
    } catch (error) {
      setError(getErrorResponse(error));
    } finally {
      setError("");
      setIsSubmitted(true);
    }
  };

  return (
    <SocialContainer>
      <Container maxWidth="md">
        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, md: 0 },
            borderRadius: 3,
            background: "white",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
          >
            {/* Left side with SVG illustration */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                background: "linear-gradient(135deg, #1976d2 0%, #050418 100%)",
                color: "white",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <LockResetRounded sx={{ fontSize: 80, mb: 2, color: "#fff" }} />
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  Forgot Your Password?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  No worries! Enter your email address and we'll send you a
                  reset link.
                </Typography>

                {/* SVG Illustration */}
                <Box sx={{ mt: 4 }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="200"
                    viewBox="0 0 600 400"
                  >
                    <defs>
                      <linearGradient
                        id="grad1"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#1976d2" />
                        <stop offset="100%" stopColor="#050418" />
                      </linearGradient>
                    </defs>
                    <path
                      fill="url(#grad1)"
                      d="M130.5,234.5c0-57.75,46.81-104.75,104.56-104.75c28.28,0,54.13,10.76,73.69,28.44l37.75-37.75l23.75,23.75 l-37.75,37.75c17.68,19.56,28.44,45.41,28.44,73.69c0,57.75-46.81,104.75-104.56,104.75S130.5,292.25,130.5,234.5z"
                    />
                    <circle fill="#FFFFFF" cx="235" cy="235" r="70" />
                    <path
                      fill="url(#grad1)"
                      d="M235,175c33.14,0,60,26.86,60,60s-26.86,60-60,60s-60-26.86-60-60S201.86,175,235,175z M255,215h-40v40h40V215z M255,195c0-11.05-8.95-20-20-20s-20,8.95-20,20h40z"
                    />
                  </svg>
                </Box>
              </Box>
            </Box>

            {/* Right side with form */}
            <Box
              sx={{
                flex: 1,
                p: 4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "700",
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #050418 100%)",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1,
                  }}
                >
                  Password Recovery
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Enter your email to reset your password
                </Typography>
              </Box>

              {isSubmitted ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Password reset instructions have been sent to your email
                  address.
                </Alert>
              ) : (
                <>
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      margin="normal"
                      variant="outlined"
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailRounded color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      size="large"
                      sx={{
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #1976d2 0%, #050418 100%)",
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #1565c0 0%, #030311 100%)",
                          boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Send Reset Link
                    </Button>
                  </Box>
                </>
              )}

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  Remember your password?{" "}
                  <Button
                    href="/login"
                    color="primary"
                    sx={{ fontWeight: 600, textTransform: "none" }}
                  >
                    Sign In
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </SocialContainer>
  );
};

export default PasswordRecovery;
